import { LockIcon, MessageCircleIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { TASK_TYPE_CONFIG } from '../data/task-config'
import type { HomeworkTask } from '../data/types'
import { useHomework } from '../providers/homework-provider'

export function TaskCard({ task }: { task: HomeworkTask }) {
  const { isCompleted, isCommentsRead, isLocked, openTask } = useHomework()

  const config = TASK_TYPE_CONFIG[task.type]
  const Icon = config.icon
  const locked = isLocked(task)
  const completed = isCompleted(task)
  const hasComments = task.comments.length > 0
  const unread = hasComments && !isCommentsRead(task)
  const primaryLink = task.links[0]

  const iconChip = (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md',
        config.chip,
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
    </span>
  )

  return (
    <div
      className={cn(
        'group relative flex w-full items-start gap-2 rounded-lg border p-2 text-left transition-colors',
        completed && !locked
          ? 'bg-muted/60 text-muted-foreground'
          : 'bg-card text-card-foreground',
        locked
          ? 'opacity-55'
          : 'hover:border-ring/60 hover:bg-accent/50 has-[button:focus-visible]:ring-[3px] has-[button:focus-visible]:ring-ring/50',
      )}
    >
      {primaryLink && !locked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={primaryLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-10 rounded-md transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
              aria-label={`Open ${primaryLink.label} in a new tab`}
            >
              {iconChip}
            </a>
          </TooltipTrigger>
          <TooltipContent>Open {primaryLink.label}</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{iconChip}</span>
          </TooltipTrigger>
          <TooltipContent>{config.label}</TooltipContent>
        </Tooltip>
      )}

      {/* Stretched button: covers the whole card, sits under the icon link */}
      <button
        type="button"
        onClick={() => openTask(task)}
        disabled={locked}
        className={cn(
          'min-w-0 flex-1 text-left after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none',
          locked && 'cursor-not-allowed',
        )}
        aria-label={`${config.label}: ${task.title}${locked ? ' (locked)' : ''}${completed ? ' (completed)' : ''}`}
      >
        <span
          className={cn(
            'line-clamp-2 text-xs leading-snug font-medium text-pretty',
            completed &&
              !locked &&
              'text-muted-foreground line-through decoration-muted-foreground/50',
          )}
        >
          {task.title}
        </span>
      </button>

      {(hasComments || locked) && (
        <span className="flex shrink-0 items-center gap-1 self-start">
          {hasComments && !locked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="relative inline-flex items-center justify-center">
                  <MessageCircleIcon
                    className={cn(
                      'size-4',
                      unread ? 'text-primary' : 'text-muted-foreground/50',
                    )}
                    aria-hidden="true"
                  />
                  {unread && (
                    <span
                      className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive"
                      aria-hidden="true"
                    />
                  )}
                  <span className="sr-only">
                    {unread ? 'Unread coach comments' : 'Coach comments (read)'}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {unread ? 'New coach feedback' : 'Coach feedback (read)'}
              </TooltipContent>
            </Tooltip>
          )}
          {locked && (
            <LockIcon
              className="size-4 text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </span>
      )}
    </div>
  )
}
